import { supabase } from './supabase';
import type { ApiResponse } from '../types/database';

export interface KnownAction {
  id: string;
  company_id: string;
  action_date: string;
  description: string;
  amount: number;
  bank_account_id: string;
  accounting_category_id: string;
  offset_code_id?: string | null;
  status: 'pending' | 'matched' | 'ignored';
  matched_bank_transaction_id?: string | null;
  /** When true the action is pinned to its original date and will NOT roll
   *  forward even if it is unmatched and the date is in the past. */
  disable_rollover?: boolean;
  /**
   * "Snooze" date — keeps the effective date at GREATEST(action_date, snoozed_until, TODAY).
   * Once TODAY passes snoozed_until the action resumes daily rolling automatically.
   * NULL = no snooze.  disable_rollover=TRUE overrides this.
   */
  snoozed_until?: string | null;
  created_at: string;
  updated_at: string;
  bank_accounts?: {
    nickname: string;
  };
  accounting_categories?: {
    name: string;
    cashflow_mapping: string;
  };
  offset_codes?: {
    name: string;
  };
}

export interface KnownActionInsert {
  company_id: string;
  action_date: string;
  description: string;
  amount: number;
  bank_account_id: string;
  accounting_category_id: string;
  offset_code_id?: string | null;
  /** Set to true to prevent automatic rollover for this action. */
  disable_rollover?: boolean;
  /** Snooze until date (YYYY-MM-DD). NULL clears the snooze. */
  snoozed_until?: string | null;
}

/**
 * Service for managing known actions
 */
class KnownActionsService {
  /**
   * Get all known actions for a company
   */
  async getKnownActions(companyId: string): Promise<ApiResponse<KnownAction[]>> {
    try {
      // Time-anchored window: start from the 1st of the previous month so that
      // unmatched items from last month are always visible, and far-future
      // projections (e.g. 2032) don't consume the row budget.
      const now = new Date();
      const startOfPreviousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        .toISOString()
        .split('T')[0]!;

      const { data, error } = await supabase
        .from('known_actions')
        .select(`
          *,
          bank_accounts (
            nickname
          ),
          accounting_categories (
            name,
            cashflow_mapping
          ),
          offset_codes (
            name
          )
        `)
        .eq('company_id', companyId)
        .gte('action_date', startOfPreviousMonth)
        .order('action_date', { ascending: true })
        .limit(2000);

      if (error) throw error;

      return {
        data: data as KnownAction[],
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error('Get known actions error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to fetch known actions',
        },
        status: 500,
      };
    }
  }

  /**
   * Create a new known action
   */
  async createKnownAction(action: KnownActionInsert): Promise<ApiResponse<KnownAction>> {
    try {
      const { data, error } = await supabase
        .from('known_actions')
        .insert(action)
        .select()
        .single();

      if (error) throw error;

      return {
        data: data as KnownAction,
        error: null,
        status: 201,
      };
    } catch (error) {
      console.error('Create known action error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to create known action',
        },
        status: 500,
      };
    }
  }

  /**
   * Update a known action
   */
  async updateKnownAction(actionId: string, updates: Partial<KnownActionInsert>): Promise<ApiResponse<KnownAction>> {
    try {
      const { data, error } = await supabase
        .from('known_actions')
        .update(updates)
        .eq('id', actionId)
        .select()
        .single();

      if (error) throw error;

      return {
        data: data as KnownAction,
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error('Update known action error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to update known action',
        },
        status: 500,
      };
    }
  }

  /**
   * Delete a known action
   */
  async deleteKnownAction(actionId: string): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('known_actions')
        .delete()
        .eq('id', actionId);

      if (error) throw error;

      return {
        data: null,
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error('Delete known action error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to delete known action',
        },
        status: 500,
      };
    }
  }

  /**
   * Bulk delete known actions
   */
  async bulkDeleteKnownActions(actionIds: string[]): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('known_actions')
        .delete()
        .in('id', actionIds);

      if (error) throw error;

      return {
        data: null,
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error('Bulk delete known actions error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to bulk delete known actions',
        },
        status: 500,
      };
    }
  }

  /**
   * Toggle action status between 'pending' and 'ignored'
   */
  async toggleActionStatus(actionId: string, currentStatus: string): Promise<ApiResponse<KnownAction>> {
    try {
      // Don't allow toggling if it's matched
      if (currentStatus === 'matched') {
        return {
          data: null,
          error: {
            message: 'לא ניתן לשנות סטטוס של פעולה מותאמת',
          },
          status: 400,
        };
      }

      const newStatus = currentStatus === 'ignored' ? 'pending' : 'ignored';

      const { data, error } = await supabase
        .from('known_actions')
        .update({ status: newStatus })
        .eq('id', actionId)
        .select()
        .single();

      if (error) throw error;

      return {
        data: data as KnownAction,
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error('Toggle action status error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to toggle action status',
        },
        status: 500,
      };
    }
  }
  /**
   * Snooze a known action until `untilDate` (YYYY-MM-DD).
   * Sets snoozed_until and keeps disable_rollover = false.
   * The effective date becomes GREATEST(action_date, untilDate, TODAY).
   */
  async snoozeUntil(actionId: string, untilDate: string): Promise<ApiResponse<KnownAction>> {
    try {
      const { data, error } = await supabase
        .from('known_actions')
        .update({ snoozed_until: untilDate, disable_rollover: false })
        .eq('id', actionId)
        .select()
        .single();

      if (error) throw error;

      return { data: data as KnownAction, error: null, status: 200 };
    } catch (error) {
      console.error('Snooze known action error:', error);
      return {
        data: null,
        error: { message: error instanceof Error ? error.message : 'Failed to snooze action' },
        status: 500,
      };
    }
  }

  /**
   * Clear the snooze on a known action (snoozed_until → NULL).
   * The action resumes normal rolling (effective date = TODAY).
   */
  async cancelSnooze(actionId: string): Promise<ApiResponse<KnownAction>> {
    try {
      const { data, error } = await supabase
        .from('known_actions')
        .update({ snoozed_until: null })
        .eq('id', actionId)
        .select()
        .single();

      if (error) throw error;

      return { data: data as KnownAction, error: null, status: 200 };
    } catch (error) {
      console.error('Cancel snooze known action error:', error);
      return {
        data: null,
        error: { message: error instanceof Error ? error.message : 'Failed to cancel snooze' },
        status: 500,
      };
    }
  }

  /**
   * Bulk insert known actions (used by the Excel import flow).
   * Returns the number of inserted rows on success.
   */
  async bulkCreateKnownActions(actions: KnownActionInsert[]): Promise<ApiResponse<{ count: number }>> {
    try {
      const { data, error } = await supabase
        .from('known_actions')
        .insert(actions)
        .select('id');

      if (error) throw error;

      return {
        data: { count: (data ?? []).length },
        error: null,
        status: 201,
      };
    } catch (error) {
      console.error('Bulk create known actions error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to bulk create known actions',
        },
        status: 500,
      };
    }
  }

  async toggleRollover(actionId: string, currentDisabled: boolean): Promise<ApiResponse<KnownAction>> {
    try {
      const { data, error } = await supabase
        .from('known_actions')
        .update({ disable_rollover: !currentDisabled })
        .eq('id', actionId)
        .select()
        .single();

      if (error) throw error;

      return {
        data: data as KnownAction,
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error('Toggle known rollover error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to toggle rollover',
        },
        status: 500,
      };
    }
  }
}

export const knownActionsService = new KnownActionsService();
